// The SINGLE source of truth for datetime handling in the app. EVERYTHING the
// operator sees or selects is America/New_York, regardless of the viewer's
// browser timezone — mirrors the backend, which is NY-native (shared/src/time.rs
// nyc()/today_nyc()/nyc_midnight(), crons in chrono_tz::America::New_York).
//
// Three data categories (see the wire types):
//   • INSTANTS   — DateTime<Utc> ISO-Z strings + most epoch-ms numbers → shown in NY.
//   • CALENDAR DAYS — `YYYY-MM-DD` strings and `day` epochs stored at 00:00:00Z →
//                     rendered as the LITERAL date, never tz-shifted.
//   • RELATIVE   — "5m ago" → timezone-independent; `ago()` below.
//
// GUARDRAIL: nothing outside this module may call toLocale*/Intl.DateTimeFormat
// or do getHours()/setHours()-style local Date math for display — a vitest
// scan enforces it. Add a helper here instead.

export const NY_TZ = 'America/New_York';
export const TZ_LABEL = 'ET';

/** A timestamp as it arrives on the wire: ISO-Z string, epoch-ms number, or Date. */
export type Instant = Date | number | string;

/** Normalize the mixed wire types to a Date. `created_at` is an ISO string on
 *  most entities but an epoch-ms number on IngestRun/IngestCorrection. */
export function toInstant(v: Instant): Date {
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);
  return new Date(v);
}

/** An instant in ISO 8601 UTC (`2026-10-12T14:05:00.000Z`), the form every CSV
 *  export writes; blank for none, and an unparseable value passes through. */
export function isoUtc(v: Instant | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  const d = toInstant(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString();
}

// ── Cached Intl formatters (NY) ─────────────────────────────────────────────
// Constructing Intl.DateTimeFormat is expensive; cache one per option-set.
const fmtCache = new Map<string, Intl.DateTimeFormat>();
function nyFormat(v: Instant, opts: Intl.DateTimeFormatOptions): string {
  const key = JSON.stringify(opts);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: NY_TZ, ...opts });
    fmtCache.set(key, f);
  }
  const d = toInstant(v);
  return Number.isNaN(d.getTime()) ? '—' : f.format(d);
}

/** "Jul 14, 2026, 2:05 PM" */
export function fmtDateTime(v: Instant): string {
  return nyFormat(v, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Jul 14, 2026" */
export function fmtDate(v: Instant): string {
  return nyFormat(v, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Jul 14" */
export function fmtDateShort(v: Instant): string {
  return nyFormat(v, { month: 'short', day: 'numeric' });
}

/** "4:28 PM" (12-hour NY wall clock; the header status line and toasts) */
export function fmtTime(v: Instant): string {
  return nyFormat(v, { hour: 'numeric', minute: '2-digit' });
}

/** "4:31:02 PM" (12-hour NY wall clock with seconds; a slot change's steps) */
export function fmtTimeSeconds(v: Instant): string {
  return nyFormat(v, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

/** "14:05" (24-hour NY wall clock) */
export function fmtHM(v: Instant): string {
  return nyFormat(v, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

/** "14:05:12" (24-hour NY wall clock) */
export function fmtClock(v: Instant): string {
  return nyFormat(v, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
}

/** "14:05:12.345" — NY wall clock with millis (log rows). */
export function fmtClockMs(v: Instant): string {
  const d = toInstant(v);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = String(nyParts(d).ms).padStart(3, '0');
  return `${fmtClock(d)}.${ms}`;
}

/** Full stamp for detail/expand views: "2026-07-14 14:05:12 ET". */
export function fmtStampEt(v: Instant): string {
  const d = toInstant(v);
  if (Number.isNaN(d.getTime())) return '—';
  const p = nyParts(d);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p.y}-${pad(p.m)}-${pad(p.d)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)} ${TZ_LABEL}`;
}

/** Append " ET" to a formatted string for labelled surfaces. */
export function withEt(s: string): string {
  return `${s} ${TZ_LABEL}`;
}

// ── Calendar-day fields (render the literal date; NO tz shift) ──────────────
// `YYYY-MM-DD` strings and `day` epochs at 00:00:00Z encode a calendar date,
// not an instant. Rendering them through NY (or browser-local) would shift the
// day. Render in UTC so the stored date comes back exactly.
const utcDayCache = new Map<string, Intl.DateTimeFormat>();
function utcDayFormat(d: Date, opts: Intl.DateTimeFormatOptions): string {
  const key = JSON.stringify(opts);
  let f = utcDayCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...opts });
    utcDayCache.set(key, f);
  }
  return f.format(d);
}

/** Parse a calendar-day value to a UTC-midnight Date (for literal rendering). */
function calendarDayToUtcDate(v: Instant): Date | null {
  if (typeof v === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
    if (m) return new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = toInstant(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Calendar day → "Jul 14, 2026" (literal date, never shifted). */
export function fmtDay(v: Instant): string {
  const d = calendarDayToUtcDate(v);
  return d ? utcDayFormat(d, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

/** Calendar day → "Jul 14" (literal date, never shifted). */
export function fmtDayShort(v: Instant): string {
  const d = calendarDayToUtcDate(v);
  return d ? utcDayFormat(d, { month: 'short', day: 'numeric' }) : '—';
}

/**
 * Inclusive literal-calendar-day keys between two validated YYYY-MM-DD values.
 *
 * Keep this arithmetic beside the rest of the calendar-day rules so report
 * screens never accidentally advance dates in the viewer's local timezone.
 */
export function calendarDayRange(from: string, to: string, maxDays = 367): string[] {
  const start = calendarDayToUtcDate(from);
  const end = calendarDayToUtcDate(to);
  if (!start || !end || start > end || maxDays <= 0) return [];
  const result: string[] = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end && result.length < maxDays) {
    result.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}


// ── NY wall-clock parts + boundary math (for selection → query params) ──────
interface NyParts {
  y: number;
  m: number; // 1-12
  d: number;
  hour: number; // 0-23
  minute: number;
  second: number;
  ms: number;
}

const partsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** The NY wall-clock components of an instant. */
export function nyParts(v: Instant): NyParts {
  const d = toInstant(v);
  const map: Record<string, string> = {};
  for (const p of partsFmt.formatToParts(d)) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return {
    y: +map.year!,
    m: +map.month!,
    d: +map.day!,
    hour: +map.hour! % 24, // some engines render midnight as "24"
    minute: +map.minute!,
    second: +map.second!,
    ms: d.getMilliseconds(), // ms is tz-invariant
  };
}

/** Offset (localWallClock − UTC) in ms for the NY zone AT the given instant.
 *  e.g. EDT → −4h → −14_400_000. */
function nyOffsetMs(utcMs: number): number {
  const map: Record<string, string> = {};
  for (const p of partsFmt.formatToParts(new Date(utcMs))) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  const asUtc = Date.UTC(
    +map.year!,
    +map.month! - 1,
    +map.day!,
    +map.hour! % 24,
    +map.minute!,
    +map.second!,
  );
  return asUtc - utcMs;
}

/** UTC epoch-ms for NY-local midnight of a calendar day (mirror of
 *  shared/src/time.rs::nyc_midnight). DST-correct via a single offset re-check. */
export function nyMidnightMs(y: number, m: number, d: number): number {
  const naiveUtc = Date.UTC(y, m - 1, d, 0, 0, 0, 0); // pretend wall clock is UTC
  const off1 = nyOffsetMs(naiveUtc);
  let t = naiveUtc - off1;
  const off2 = nyOffsetMs(t); // re-check in case naiveUtc/t straddle a DST change
  if (off2 !== off1) t = naiveUtc - off2;
  return t;
}

/** Today's NY calendar date. */
export function nyToday(): { y: number; m: number; d: number } {
  const p = nyParts(new Date());
  return { y: p.y, m: p.m, d: p.d };
}

/** UTC epoch-ms of the start of the NY day containing `v`. */
export function nyStartOfDayMs(v: Instant): number {
  const p = nyParts(v);
  return nyMidnightMs(p.y, p.m, p.d);
}

/** UTC epoch-ms of the (exclusive) end of the NY day containing `v` — i.e. the
 *  start of the next NY day. Handles 23h/25h DST days correctly. */
export function nyEndOfDayMsExclusive(v: Instant): number {
  const start = nyStartOfDayMs(v);
  // +36h lands solidly inside the next NY day regardless of DST length; snap it.
  return nyStartOfDayMs(start + 36 * 3_600_000);
}

/** ISO-Z string for the start of the NY day containing `v` (for `from` params). */
export function nyStartOfDayIso(v: Instant): string {
  return new Date(nyStartOfDayMs(v)).toISOString();
}

/** ISO-Z string for the last millisecond of the NY day containing `v` (`to`). */
export function nyEndOfDayIso(v: Instant): string {
  return new Date(nyEndOfDayMsExclusive(v) - 1).toISOString();
}

/** `YYYY-MM-DD` (NY calendar day) for an `<input type="date">` value. Replaces
 *  the tz-shifting `toISOString().slice(0,10)`. */
export function fmtDateInput(v: Instant): string {
  const p = nyParts(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

/** NY today as `YYYY-MM-DD` — for `<input type="date">` min/max bounds, export
 *  filenames, and other functional "today in NY" needs (replaces the tz-shifting
 *  `new Date().toISOString().slice(0,10)`). */
export function todayNyInput(): string {
  return fmtDateInput(new Date());
}

/** The literal `YYYY-MM-DD` of a CALENDAR-DAY value (a `YYYY-MM-DD` string, or a
 *  day-epoch at 00:00:00Z) — UTC-basis, no tz shift. For CSV cells / sort keys
 *  where the sortable date string (not a human label) is wanted. */
export function dayYmd(v: Instant): string {
  const d = calendarDayToUtcDate(v);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Interpret an `<input type="date">` value (`YYYY-MM-DD`) as an NY calendar day
 *  → UTC epoch-ms of its NY midnight. Returns NaN for malformed input. */
export function nyDateInputToMs(ymd: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return NaN;
  return nyMidnightMs(+m[1]!, +m[2]!, +m[3]!); // nyMidnightMs takes month 1-12
}

// ── Relative deltas (timezone-independent) — the ONE consolidated helper ────
/** "just now" / "5m ago" / "3h ago" / "2d ago". Accepts any Instant. */
export function ago(v: Instant, now: number = Date.now()): string {
  const t = toInstant(v).getTime();
  if (!Number.isFinite(t)) return '—';
  const s = (now - t) / 1000;
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/** Conservative freshness check for provider evidence; future timestamps fail closed. */
export function isInstantFresh(value: Instant | null | undefined, maxAgeMs: number, now = Date.now()): boolean {
  if (value == null) return false;
  const age = now - toInstant(value).getTime();
  return Number.isFinite(age) && age >= 0 && age < maxAgeMs;
}
